import Pikaday from "pikaday";

class EventCalendar {
	constructor(element) {
		this.el = element;
		this.sidebar = this.el.querySelector(".event-archive__sidebar");
		this.filterForm = this.sidebar.querySelector("form");
		this.controls = this.el.querySelector(
			".event-archive__filter-controls"
		);
		this.filterOptions = this.sidebar.querySelectorAll("select");
		this.filterSubmit = this.sidebar.querySelector(`input[type="submit"]`);
		this.filterReset = this.sidebar.querySelector(`input[type="reset"]`);
		this.calendarEl = this.sidebar.querySelector("#calendar");
		this.calendarPicker;
		this.startDateEl = this.sidebar.querySelector("#start-date-select");
		this.endDateEl = this.sidebar.querySelector("#end-date-select");
		this.isEndDate = false;
		this.startDate = null;
		this.endDate = null;
		this.shouldReset = false;
		this.mobileShowButton = document.querySelector(".show-filter");
		this.mobileCloseButton = document.querySelector(".close-filter");
		this.events = this.el.querySelectorAll(".event-card");
		this.recurringEvents = this.el.querySelectorAll(
			".event-card[data-recurring-event]"
		);
		this.init();
	}

	init() {
		// initial setup
		const searchParams = new URLSearchParams(window.location.search);
		const startDate = searchParams.get("start_date");
		const endDate = searchParams.get("end_date");

		// set up date picker
		this.calendarPicker = new Pikaday({
			field: this.calendarEl,
			bound: false,
			toString: function (date, format) {
				// set up to string function to spit out WP query friendly formatting

				const d = date.getDate();
				const md = date.getMonth() + 1;

				// day and month need to prepend a 0 if
				const Day = d > 9 ? d : `0${d}`;
				const Month = md > 9 ? md : `0${md}`;

				const Year = date.getFullYear();

				return `${Year}-${Month}-${Day}`;
			},
			onSelect: function (date) {
				if (
					(this.startDate != null && this.endDate != null) ||
					(this.startDate != null && date < this.startDate) ||
					(this.endDate != null && date > this.endDate)
				) {
					// reset date picker and then set the selected date as the new starting date
					this.startDate = null;
					this.endDate = null;
					this.calendarPicker.clear();
					this.calendarPicker.setStartRange();
					this.calendarPicker.setEndRange();
					this.calendarPicker.setDate(date);
					this.startDateEl.value = this.calendarPicker.toString();
					this.endDateEl.value = "";
				} else if (this.startDate === null) {
					// if start date is not set, we can assume the incoming date to be the new start
					this.startDate = date;
					this.calendarPicker.setStartRange(date);
					this.startDateEl.value = this.calendarPicker.toString();
				} else if (this.startDate != null && this.endDate == null) {
					this.endDate = date;
					this.calendarPicker.setEndRange(date);
					this.calendarPicker.setDate(date, true);
					this.endDateEl.value = this.calendarPicker.toString();
				}
			}.bind(this),
		});

		if (startDate && endDate) {
			// calendar onSelect function should deal with setting ranges and such
			// need to append time to ensure we're getting the right date
			this.calendarPicker.setDate(`${startDate}T00:00:00`);
			this.calendarPicker.setDate(`${endDate}T00:00:00`);
		} else if (startDate && !endDate) {
			this.calendarPicker.setDate(`${startDate}T00:00:00`);
		}

		this.filterForm.addEventListener("submit", (e) => {
			e.preventDefault();
			const inputs = this.filterForm.querySelectorAll("input, select");

			inputs.forEach((i) => {
				if (i.value == "" || i.value == "All" || i.value == null) {
					i.setAttribute("disabled", true);
				}
			});

			this.filterForm.submit();
		});

		this.filterReset.addEventListener("click", (e) => {
			e.preventDefault();
			// need to reset the calendar and all select inputs on reset
			this.startDate = null;
			this.endDate = null;
			this.calendarPicker.clear();
			this.calendarPicker.setStartRange();
			this.calendarPicker.setEndRange();
			this.calendarPicker.setDate();
			this.startDateEl.value = "";
			this.startDateEl.setAttribute("value", "");
			this.endDateEl.value = "";

			this.filterOptions.forEach((o) => {
				const sls = o.querySelectorAll("option");

				sls.forEach((s) => {
					s.removeAttribute("selected");
				});
			});

			// this.filterForm.requestSubmit();
			this.filterForm.dispatchEvent(new Event("submit"));
		});

		// hide and show the filter controls on mobile
		this.mobileShowButton.addEventListener("click", () => {
			this.sidebar.classList.add("open");
			document.body.classList.add("noscroll");
			document.documentElement.classList.add("noscroll");
		});

		this.mobileCloseButton.addEventListener("click", () => {
			this.sidebar.classList.remove("open");
			document.body.classList.remove("noscroll");
			document.documentElement.classList.remove("noscroll");
		});

		this.initRecurringEvents();
	}

	initRecurringEvents() {
		// filter through recurring events
		// determine the frequency of each event
		// create a list of viable dates before its end date
		// if any of those dates are on the selected date or falls within a date range
		// the event should be visible
		// otherwise the event should be hidden

		// if a start date hasn't been selected, don't do anything
		if (!this.startDate) {
			this.recurringEvents.forEach((e) => {
				e.classList.remove("hidden");
			});

			return;
		}

		this.recurringEvents.forEach((e) => {
			// convert dataset start to Date
			const eventStart = e.dataset.eventStart;
			const startDate = new Date(eventStart);

			// same with end
			const eventEnd = e.dataset.eventEnd;
			const endDate = new Date(eventEnd);

			// weekly or monthly
			const frequency = e.dataset.recurringFrequency;

			// if monthly, on a day or date
			const recurrence = e.dataset.recurrence;

			// if monthly on a day, which weekday
			const weekDay = e.dataset.dayWeekday;
			// and which week... kinda...
			const dayIndex = e.dataset.dayIndex;

			const es = startDate.getDate();
			const ss = this.startDate.getDate();
			const ee = endDate.getDate();

			// in all cases we know we are getting events from the server that match the search criteria
			// so we don't need to do any further filtering beyond determining whether a recurring event should show up based on the search params

			// recurring events are hidden by default

			// not pleased with this. if you're angry about it down the line, i feel you. -kj
			if (frequency == "weekly") {
				// weekly recurring events

				const eventDay = startDate.getDay();
				const selectedStart = this.startDate.getDay();

				// if there is an end date, we know the user has selected a date range
				if (this.endDate != null) {
					const selectedEnd = this.endDate.getDay();

					if (selectedStart <= eventDay && selectedEnd >= eventDay) {
						e.classList.remove("hidden");
					}
				} else if (
					!this.endDate &&
					selectedStart == startDate.getDay()
				) {
					e.classList.remove("hidden");
				}
			} else if (frequency == "monthly" && recurrence == "day") {
				// if the event occurs on a day, i.e. "first tuesday of the month"
				const td = this.nthWeekdayOfMonth(
					weekDay,
					dayIndex,
					this.startDate
				);

				const date = td.getDate();

				if (!this.endDate) {
					if (td.toDateString() == this.startDate.toDateString()) {
						e.classList.remove("hidden");
					}
				} else if (date >= ss && date <= this.endDate.getDate()) {
					e.classList.remove("hidden");
				}
			} else if (frequency == "monthly" && recurrence == "date") {
				if (!this.endDate) {
					if (es == ss) {
						e.classList.remove("hidden");
					}
				} else if (es >= ss && es <= ee) {
					e.classList.remove("hidden");
				}
			}
		});
	}

	// SO crud to find a given day of a month
	// i.e. second tuesday of the month
	// https://stackoverflow.com/a/35470102
	// -KJ
	nthWeekdayOfMonth(weekday, idx, date) {
		var month = date.getMonth();
		var date = new Date(date.getFullYear(), month, 1),
			add = ((weekday - date.getDay() + 7) % 7) + (idx - 1) * 7;

		// if the date overflows to the next month, we're looking for the last date
		do {
			date.setMonth(month);
			date.setDate(1 + add);
			add -= 7;
		} while (date.getMonth() != month);

		return date;
	}
}

window.addEventListener("DOMContentLoaded", () => {
	if (
		document.readyState == "complete" ||
		document.readyState == "interactive"
	) {
		let eventArchive = document.querySelector(".event-archive");

		if (eventArchive) {
			new EventCalendar(eventArchive);
		}
	}
});



// WEBPACK FOOTER //
// ./scripts/event-filter.js